/**
 * Swap the poster facade for the real YouTube player.
 *
 * Nothing has been requested from YouTube up to this point — the poster is a
 * local image — so the embed only exists from the click onwards. That click is
 * also the user gesture `autoplay=1` needs, which is why the film starts
 * without a second press. `youtube-nocookie` is the host that holds off on
 * tracking until playback, and `playsinline` keeps iOS from taking the video
 * fullscreen on its own.
 */
function playEmbed(wrapper) {
    if (wrapper.querySelector('iframe')) return

    const id = encodeURIComponent(wrapper.dataset.youtubeId)
    const frame = document.createElement('iframe')

    frame.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`
    frame.title = wrapper.dataset.videoTitle || 'Video'
    frame.allow =
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
    frame.allowFullscreen = true
    // The live server sends `Referrer-Policy: no-referrer` for the whole domain,
    // and YouTube refuses an embed it can't attribute to a site (Error 153).
    // The element's own policy wins over the document's, and sends only the origin.
    frame.referrerPolicy = 'strict-origin-when-cross-origin'
    frame.className = 'absolute inset-0 size-full'

    wrapper.append(frame)
    wrapper.classList.add('is-playing')
}

/**
 * Start the self-hosted file.
 *
 * Native `controls` are handed over on play rather than shipped in the markup,
 * which is what lets the circular button be the only control at rest.
 */
function playFile(wrapper) {
    const video = wrapper.querySelector('[data-video-player]')
    if (!video) return

    wrapper.classList.add('is-playing')
    video.controls = true

    const played = video.play()
    if (!played) return

    // play() rejects when the source can't be loaded, and the overlay is already
    // gone by then. Put it back, or the section is a still frame with nothing
    // left to click.
    played.catch(() => {
        wrapper.classList.remove('is-playing')
        video.controls = false
    })
}

/**
 * Gallery video — the design's play button over a poster.
 *
 * Two sources behind one control, chosen by the `$video` config in
 * page-gallery.php. A `data-youtube-id` on the wrapper means the poster is a
 * facade for an embed; without it the wrapper holds a self-hosted `<video>`.
 */
export function initGalleryVideo() {
    const wrapper = document.querySelector('[data-video]')
    if (!wrapper) return

    const trigger = wrapper.querySelector('[data-video-play]')
    if (!trigger) return

    trigger.addEventListener('click', () => {
        if (wrapper.dataset.youtubeId) playEmbed(wrapper)
        else playFile(wrapper)
    })
}
